import { setLicenseKey } from '@grapecity/wijmo';
import { FlexGrid, CellType, Row, CellRange, GridPanel, CellRangeEventArgs } from '@grapecity/wijmo.grid';
import { WijmoLicense } from '../wijmo-license';

/**
 * Wijmoに関する便利機能を提供するUtil
 */
export class WijmoUtils {

    /**
     * Wijmoのライセンスキーを設定する
     *
     * @returns キー設定成功時はtrueを返す
     */
    static setLicenseKey(): boolean {
        // ライセンスキーはホスト名ごとに発行される為、ホスト名に対応したキーを取得して設定する
        const hostname = window.location.hostname;
        const licenseKey: string = WijmoLicense.LICENSE_KEY[hostname];
        if (licenseKey != null) {
            setLicenseKey(licenseKey);
            return true;    // ホストに対応するキーを取得・設定した場合はtrue
        }
        return false;       // ホストに体操するキーが取得できない場合はfalse
    }

    /**
     * 【★24】FlexGridで複数行選択(SelectionMode.ListBox指定)時の挙動をFlexに近づけるための設定を行う
     * @param list 複数選択するFlexGrid
     */
    static setMultiSelectConfig(list: FlexGrid) {
        list.hostElement.addEventListener('mousedown', (e: MouseEvent) => {
            if (e.button === 0) {   // 左クリック時のみ処理する
                const ht = list.hitTest(e);
                if (ht.cellType === CellType.Cell) {    // セルのみ処理対象とする
                    const row: Row = list.rows[ht.row];
                    if (!e.shiftKey) {  // Shitキー押下時はFlexGridの処理そのままとする
                        if (e.ctrlKey) {
                            // 最初に選択した行をCtrlを押しながら選択解除すると
                            // 行は解除されるが、セルが選択状態になってしまう問題に対応
                            if (!row.isSelected) {
                                const selected = list.selectedRows;         // 行の選択状態をバックアップ
                                list.select(new CellRange(-1, -1), false);  // セルの選択を解除する為に選択状態をクリア
                                list.selectedRows = selected;               // 行の選択状態を復元
                                list.refresh();
                            }
                        } else {
                            // 行クリック時、通常は他の行が選択解除されるが
                            // 最初に選択した行をクリックすると他の行が選択解除されない問題に対応
                            if (list.selectedRows.length > 1) { // 複数行選択している場合
                                list.selectedRows = [row];      // クリックした行のみ選択状態にする(他の行を選択解除する)
                                list.refresh();
                            }
                        }
                    }
                }
            }
        });
    }

    /**
     * 【★10】FlexGridで複数行選択(SelectionMode.ListBox指定)＋ドラッグ操作時の挙動をFlexに近づけるための設定を行う
     * @param list 複数選択するFlexGrid
     * @param selectionChangeCancelFlag 選択状態変更をキャンセルしたかどうかを管理するフラグ
     */
    static setMultiSelectAndDragConfig(list: FlexGrid, selectionChangeCancelFlag: boolean) {
        /* 各セルをドラッグ可能に設定 */
        list.itemFormatter = (panel: GridPanel, r: number, c: number, cell: HTMLElement) => {
            if (panel.cellType === CellType.Cell) {
                cell.draggable = true;
            }
        };
        /* ドラッグする為にクリックすると選択状態が解除されるのを防止する為の処理 */
        list.selectionChanging.addHandler((sender: FlexGrid, args: CellRangeEventArgs) => {
            if (list.selectedRows.length > 1) {     // 複数行選択状態の場合のみ処理
                const row: Row = list.rows[args.row];
                if (row != null && row.isSelected) {
                    // 選択状態の行を選ばれた場合、選択状態変更をキャンセル
                    args.cancel = true;
                    selectionChangeCancelFlag = true;  // 選択状態変更をキャンセルしたことを示すフラグをONに
                }
            }
        });
        /* 選択状態変更がキャンセルされなかった場合、マウスが押された瞬間に複数行選択をFlexの挙動に近づける処理を実行 */
        list.hostElement.addEventListener('mousedown', (e: MouseEvent) => {
            if (!selectionChangeCancelFlag) {    // 選択状態変更がキャンセルされていない場合のみ実行
                if (e.button === 0) {   // 左クリック時のみ処理する
                    const ht = list.hitTest(e);
                    if (ht.cellType === CellType.Cell) {  // セルのみを処理対象とする
                        const row: Row = list.rows[ht.row];
                        // Shiftキーが押されていない場合(Shifキーが押されている場合はWijmo標準の処理を実行)
                        if (!e.shiftKey) {
                            if (e.ctrlKey) {
                                // Ctrlキーが押されている場合
                                // 最初に選択した行をCtrlを押しながら選択解除すると
                                // 行は選択解除されるが、セルが選択状態になってしまう問題に対応
                                const selected = list.selectedRows;         // 行の選択状態をバックアップ
                                list.select(new CellRange(-1, -1), false);  // セルの選択を解除する為に選択状態をクリア
                                list.selectedRows = selected;               // 行の選択状態を復元
                                list.refresh();
                            } else {
                                // Ctrlキーが押されていない場合(単純なクリックの場合)
                                // 行クリック時、通常は他の行が選択解除されるが
                                // 最初に選択した行をクリックすると他の行が選択解除されない問題に対応
                                if (list.selectedRows.length > 1) {           // 複数行選択している場合のみ実行
                                    list.select(new CellRange(-1, -1), false);  // 選択状態をクリア
                                    list.selectedRows = [row];                  // クリックした行のみ選択状態にする(他の行を選択解除する)
                                    list.refresh();
                                }
                            }
                        }
                    }
                }
            }
        });
        /* ドラッグ対策のため選択状態変更がキャンセルされた場合、マウスボタンが上がった時にキャンセルした選択状態変更処理を実行 */
        list.hostElement.addEventListener('mouseup', (e: MouseEvent) => {
            if (selectionChangeCancelFlag) {   // 選択状態変更がキャンセルされた場合のみ実行
                if (e.button === 0) {     // 左クリック時のみ処理する
                    selectionChangeCancelFlag = false;
                    const ht = list.hitTest(e);
                    if (ht.cellType === CellType.Cell) {      // セルのみを処理対象とする
                        const row: Row = list.rows[ht.row];
                        if (!e.shiftKey && !e.ctrlKey) {
                            // Shitキー・Ctrlキーとも押されていない場合、クリックされた行のみを選択状態にする
                            list.select(new CellRange(-1, -1), false);
                            list.selectedRows = [row];
                            list.refresh();
                        } else if (!e.shiftKey && e.ctrlKey) {
                            // Ctrlキーのみ押されている場合、クリックされた行を選択解除する
                            const selected = list.selectedRows;
                            const index = selected.indexOf(row);
                            if (index !== -1) {
                                selected.splice(index, 1);
                                list.select(new CellRange(-1, -1), false);
                                list.selectedRows = selected;
                                list.refresh();
                            }
                        } else if (e.shiftKey && !e.ctrlKey) {
                            // Shitキーのみ押されている場合、クリックされた行までを範囲選択状態にする
                            list.selectedRows = [];
                            list.select(new CellRange(ht.row, -1, list.selection.row2, -1), false);
                            list.refresh();
                        }
                    }
                }
            }
        });
        /* 複数行選択可能時のFlexGridの標準機能であるマウス移動による連続選択をキャンセルするための処理 */
        list.hostElement.addEventListener('mousemove', (e: MouseEvent) => {
            e.stopPropagation();  // 上位へのイベントの伝播をキャンセル
        });
    }
}
